import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import AdminAppBody from '../../../../components/admin/AdminAppBody';
import PageHeading from '../../../../components/admin/common/PageHeading';
import { useParams, useNavigate } from 'react-router-dom';
import useApiCallHooks from '../../../../hooks/useApiCallHooks';
import { toast } from 'react-toastify';

const QuizEdit = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    level: '',
    useExcel: false,
    image: '',
    age: '',
    tag: '',
    rating: ''
  });

  const [questions, setQuestions] = useState([
    {
      question: '',
      options: ['', '', '', ''],
      answer: ''
    }
  ]);

  const [submitted, setSubmitted] = useState(false);
  const [responce, loading, error, callAPI, statusCode] = useApiCallHooks();

  useEffect(() => {
    callAPI('get', `admin/get-quiz-questions/${slug}`, '');
  }, [slug]);

  useEffect(() => {
    if (responce?.data && statusCode === 200 && submitted) {
      toast.success('Quiz updated successfully!');
      setTimeout(() => {
        navigate('/robotic-syallbus/list');
      }, 2000);
    }
  }, [responce, statusCode, submitted, navigate]);

  const handleQuestionChange = (index, field, value) => {
    const updated = [...questions];
    if (field === 'question') {
      updated[index].question = value;
    } else if (field === 'answer') {
      updated[index].answer = value;
    } else {
      updated[index].options[field] = value;
    }
    setQuestions(updated);
  };

  const addQuestion = () => {
    setQuestions([
      ...questions,
      {
        question: '',
        options: ['', '', '', ''],
        answer: ''
      }
    ]);
  };

  const deleteQuestion = (index) => {
    const updated = [...questions];
    updated.splice(index, 1);
    setQuestions(updated);
  };

  const handleExcelFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (evt) => {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: 'array' });

      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const parsedQuestions = jsonData.map((row) => ({
        question: row.question || '',
        options: [
          row.option1 || '',
          row.option2 || '',
          row.option3 || '',
          row.option4 || ''
        ],
        answer: row.answer || ''
      }));

      setQuestions(parsedQuestions.length ? parsedQuestions : [
        {
          question: '',
          options: ['', '', '', ''],
          answer: ''
        }
      ]);
    };

    reader.readAsArrayBuffer(file);
  };

  const downloadTemplate = () => {
    const worksheetData = [
      {
        question: 'What is 2 + 2?',
        option1: '3',
        option2: '4',
        option3: '5',
        option4: '6',
        answer: '4'
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
    XLSX.writeFile(workbook, 'quiz_template.xlsx');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const cleanedData = {
      ...formData,
      questions
    };

    callAPI('post', `admin/update-quiz/${slug}`, cleanedData);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  return (
    <AdminAppBody
      content={
        <div className="container">
          <PageHeading title="Edit Quiz" subTitle="Edit and change quiz details" />

          <div className="container card p-4 shadow-sm mt-3">
            <form onSubmit={handleSubmit}>
              {/* Basic Info */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">Quiz Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Image URL</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  />
                  {formData.image && (
                    <div className="mt-2">
                      <img
                        src={formData.image}
                        alt="Quiz Preview"
                        className="img-fluid rounded border"
                        style={{ maxHeight: '150px' }}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">Tag</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.tag}
                    onChange={(e) => setFormData({ ...formData, tag: e.target.value })}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Rating</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.rating}
                    onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
                  />
                </div>
              </div>

              {/* Question Input Method */}
              <div className="row mb-3">
                <div className="col-12 mb-3">
                  <label className="form-label fw-bold">Choose Question Input Method</label>
                  <div className="row">
                    <div className="form-check col-md-6">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="questionInput"
                        id="inputExcel"
                        checked={formData.useExcel === true}
                        onChange={() => setFormData({ ...formData, useExcel: true })}
                      />
                      <label className="form-check-label" htmlFor="inputExcel">
                        Upload via Excel Sheet
                      </label>
                    </div>
                    <div className="form-check col-md-6">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="questionInput"
                        id="inputManual"
                        checked={formData.useExcel === false}
                        onChange={() => setFormData({ ...formData, useExcel: false })}
                      />
                      <label className="form-check-label" htmlFor="inputManual">
                        Enter Questions Manually
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Excel Upload */}
              {formData.useExcel && (
                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label">Upload Excel File</label>
                    <div className="d-flex gap-3 mb-2">
                      <input
                        type="file"
                        accept=".xls,.xlsx"
                        className="form-control"
                        onChange={handleExcelFile}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={downloadTemplate}
                      >
                        Download Template
                      </button>
                    </div>
                    <small className="text-muted">
                      Excel format: <strong>question, option1, option2, option3, option4, answer</strong>
                    </small>
                  </div>
                </div>
              )}

              {/* Manual Entry */}
              {!formData.useExcel && (
                <div>
                  {questions.map((q, index) => (
                    <div key={index} className="card p-3 mb-3 border">
                      <div className="mb-2">
                        <label className="form-label">Question {index + 1}</label>
                        <input
                          type="text"
                          className="form-control"
                          value={q.question}
                          onChange={(e) => handleQuestionChange(index, 'question', e.target.value)}
                        />
                      </div>
                      <div className="row">
                        {[0, 1, 2, 3].map((optIndex) => (
                          <div className="col-md-6 mb-2" key={optIndex}>
                            <label className="form-label">Option {optIndex + 1}</label>
                            <input
                              type="text"
                              className="form-control"
                              value={q.options[optIndex]}
                              onChange={(e) =>
                                handleQuestionChange(index, optIndex, e.target.value)
                              }
                            />
                          </div>
                        ))}
                      </div>
                      <div className="mb-2">
                        <label className="form-label">Correct Answer</label>
                        <select
                          className="form-select"
                          value={q.answer}
                          onChange={(e) =>
                            handleQuestionChange(index, 'answer', e.target.value)
                          }
                        >
                          <option value="">Select correct answer</option>
                          {q.options.map((opt, i) => (
                            <option key={i} value={opt}>
                              {opt || `Option ${i + 1}`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="text-end">
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => deleteQuestion(index)}
                        >
                          Delete Question
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="text-start mb-3">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={addQuestion}
                    >
                      + Add Question
                    </button>
                  </div>
                </div>
              )}

              {/* Submit */}
              <div className="text-end">
                <button type="submit" className="btn btn-primary">
                  Submit Quiz
                </button>
              </div>
            </form>
          </div>
        </div>
      }
    />
  );
};

export default QuizEdit;
